import { Card, CardContent } from "@/components/ui/card";
import { Heart, Users, Target, Clock, Sparkles } from "lucide-react";

export default function AboutPage() {
  const values = [
    {
      icon: Heart,
      title: "Compassion",
      description: "We approach every family with empathy and understanding, recognizing the stress and uncertainty they face."
    },
    {
      icon: Users,
      title: "Community",
      description: "We believe in the power of community coming together to support those in need."
    },
    {
      icon: Target,
      title: "Impact",
      description: "Every action we take is focused on creating meaningful, measurable impact for families."
    },
    {
      icon: Clock,
      title: "Reliability",
      description: "Families can count on us to be there when they need support most."
    }
  ];

  const timeline = [
    {
      year: "The Spark",
      title: "Recognizing the Need",
      description: "After watching families struggle to find affordable lodging near hospitals, our founder knew something had to change."
    },
    {
      year: "Building",
      title: "Laying the Foundation",
      description: "We're actively reaching out to hotels willing to donate unused rooms to families in need."
    },
    {
      year: "Growing",
      title: "Expanding Services",
      description: "Our vision includes transportation assistance and daily necessities support for families."
    },
    {
      year: "Goal",
      title: "20 Hotel Partners",
      description: "We aim to build a network of 20 hotel partners providing rooms across the region."
    },
    {
      year: "Vision",
      title: "500 Families Served",
      description: "Our ultimate goal is to help 500 families stay close to their hospitalized loved ones."
    }
  ];

  return (
    <div className="relative">
      {/* Hero Section */}
      <section className="relative py-24 lg:py-32 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1662549904986-0b49849f2bea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwxfHxkaXZlcnNlJTIwZ3JvdXAlMjBvZiUyMGhhcHB5JTIwdm9sdW50ZWVyc3xlbnwwfHx8fDE3NzAzNDc1NzN8MA&ixlib=rb-4.1.0&q=85')` 
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background via-background/95 to-background" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl space-y-6 animate-fade-in-up">
            <span className="text-accent font-medium text-sm uppercase tracking-wider">About Us</span>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-tight">
              Our Mission is <span className="text-primary">Hope</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Hope Hospitality exists to ensure that no family has to choose between being 
              with their hospitalized loved one and having a safe, comfortable place to stay.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-16 bg-primary">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <Sparkles className="w-12 h-12 text-primary-foreground/80 mx-auto mb-6" />
          <blockquote className="font-serif text-2xl md:text-3xl text-primary-foreground italic leading-relaxed" data-testid="mission-statement">
            "To provide comfort, support, and hope to families of hospital patients by 
            connecting them with lodging, transportation, and daily necessities—because 
            no one should face their hardest days alone."
          </blockquote>
          <p className="text-primary-foreground/70 mt-6 text-sm uppercase tracking-wider">
            Our Mission Statement
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <span className="text-accent font-medium text-sm uppercase tracking-wider">Our Story</span>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-foreground">
                Why We Started
              </h2>
              <div className="space-y-4 text-muted-foreground leading-relaxed">
                <p>
                  Hope Hospitality was born from a simple observation: families traveling 
                  to visit loved ones in the hospital often face impossible choices. They 
                  arrive in unfamiliar cities, uncertain of how long they'll need to stay, 
                  and quickly discover that hotel costs can devastate already stretched budgets.
                </p>
                <p>
                  Meanwhile, hotels regularly have unused rooms—inventory that goes unsold 
                  night after night. We saw an opportunity to connect these dots: to transform 
                  empty rooms into havens of rest for families in crisis.
                </p>
                <p>
                  Our founder, after experiencing the stress of finding lodging during a 
                  family medical emergency firsthand, decided to create the solution they 
                  wished had existed. What started as conversations with local hotel managers 
                  has grown into a network of compassionate partners committed to serving 
                  families in their time of need.
                </p>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1662549904986-0b49849f2bea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwxfHxkaXZlcnNlJTIwZ3JvdXAlMjBvZiUyMGhhcHB5JTIwdm9sdW50ZWVyc3xlbnwwfHx8fDE3NzAzNDc1NzN8MA&ixlib=rb-4.1.0&q=85"
                alt="Volunteers working together"
                className="rounded-2xl shadow-lg"
              />
              <div className="absolute -bottom-6 -left-6 bg-accent text-accent-foreground px-6 py-4 rounded-xl shadow-lg">
                <div className="font-serif text-3xl font-bold">500</div>
                <div className="text-sm">Families to Serve</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 lg:py-32 bg-secondary">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-accent font-medium text-sm uppercase tracking-wider">Our Values</span>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-foreground mt-3">
              What Guides Us
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <Card 
                key={index} 
                className="bg-card border-border/50 card-hover"
                data-testid={`value-card-${index}`}
              >
                <CardContent className="p-8 text-center space-y-4">
                  <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mx-auto">
                    <value.icon className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="font-serif text-xl font-bold text-foreground">
                    {value.title}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {value.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 lg:py-32">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <span className="text-accent font-medium text-sm uppercase tracking-wider">Our Roadmap</span>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-foreground mt-3">
              Where We're Going
            </h2>
          </div>

          <div className="relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-border" />
            
            <div className="space-y-12">
              {timeline.map((item, index) => (
                <div 
                  key={index} 
                  className={`relative flex items-start gap-8 ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                  data-testid={`timeline-item-${index}`}
                >
                  <div className="hidden md:block flex-1" />
                  
                  <div className="absolute left-4 md:left-1/2 w-3 h-3 bg-primary rounded-full transform -translate-x-1/2 mt-2" />
                  
                  <div className="flex-1 ml-12 md:ml-0">
                    <Card className="bg-card border-border/50">
                      <CardContent className="p-6">
                        <span className="text-accent font-bold text-lg">{item.year}</span>
                        <h3 className="font-serif text-xl font-bold text-foreground mt-1">
                          {item.title}
                        </h3>
                        <p className="text-muted-foreground mt-2 text-sm leading-relaxed">
                          {item.description}
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
